import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from "@angular/core";
import { DispatchContact } from "@app/shared/models/clients/BillToViewModel.model";

@Component({
  selector: "app-client-billto",
  templateUrl: "./client-billto.component.html",
  styleUrls: ["./client-billto.component.css"],
})
export class ClientBilltoComponent implements OnInit, OnChanges {
  @Input() client: any;
  @Input() isEditMode: boolean = false;
  @Input() states: any[];
  @Input() phoneMask: any;
  @Input() dateInputMask: any;
  @Input() zipMask: any;
  @Input() canViewProfileTab: boolean = true;
  @Input() canViewContactsTab: boolean = true;
  @Input() canViewBillingTab: boolean = true;
  @Input() canEditProfileTab: boolean = true;
  @Input() canEditContactsTab: boolean = true;
  @Input() canEditBillingTab: boolean = true;
  @Output() hasChanges = new EventEmitter<boolean>();
  @Output() newBillTosChange = new EventEmitter<any[]>();
  @Output() editedBillTosChange = new EventEmitter<any[]>();
  @Output() removedBillTosChange = new EventEmitter<any[]>();

  searchText: string = "";
  showInactive: boolean = false;
  selectedBillTo: any = null;
  selectedBillToTab: string = "profile";
  filteredBillTos: any[] = [];

  // Track bill-to changes
  private newBillTos: any[] = [];
  private editedBillToIds: Set<string> = new Set();
  private removedBillTos: any[] = [];

  // Dispatch contacts for selected bill to
  dispatchContacts: DispatchContact[] = [];

  // Invoice emails as array for line-by-line editing
  invoiceEmails: string[] = [""];

  ngOnInit() {
    this.filterBillTos();
  }

  ngOnChanges() {
    this.filterBillTos();
  }

  filterBillTos() {
    if (!this.client?.children) {
      this.filteredBillTos = [];
      return;
    }

    let billTos = [...this.client.children];

    // Filter by active status
    if (!this.showInactive) {
      billTos = billTos.filter((billTo: any) => this.isBillToActive(billTo));
    }

    // Filter by search text
    if (this.searchText) {
      const searchLower = this.searchText.toLowerCase();
      billTos = billTos.filter(
        (billTo: any) =>
          billTo.companyName?.toLowerCase().includes(searchLower) ||
          billTo.customerNo?.toLowerCase().includes(searchLower)
      );
    }

    this.filteredBillTos = billTos;
  }

  isBillToActive(billTo: any): boolean {
    const now = new Date();
    const validFrom = billTo.validFrom ? new Date(billTo.validFrom) : null;
    const validTo = billTo.validTo ? new Date(billTo.validTo) : null;

    if (validFrom && validFrom > now) {
      return false;
    }
    if (validTo && validTo < now) {
      return false;
    }
    return true;
  }

  selectBillTo(billTo: any) {
    this.selectedBillTo = billTo;
    this.selectedBillToTab = "profile";
    this.loadDispatchContacts();
    this.loadInvoiceEmails();
  }

  backToList() {
    this.selectedBillTo = null;
    this.selectedBillToTab = "profile";
    this.dispatchContacts = [];
    this.invoiceEmails = [""];
  }

  selectBillToTab(tab: string) {
    this.selectedBillToTab = tab;
  }

  // Dispatch Contacts Methods
  loadDispatchContacts() {
    // Load from selectedBillTo.dispatchContacts if it exists
    if (this.selectedBillTo?.dispatchContacts) {
      this.dispatchContacts = [...this.selectedBillTo.dispatchContacts];
    } else {
      this.dispatchContacts = [];
    }
  }

  addDispatchContact() {
    this.dispatchContacts.push({
      name: "",
      type: "",
      phone: "",
      hours: "",
      email: "",
    });
    this.syncDispatchContacts();
    this.onBillToChange();
  }

  removeDispatchContact(index: number) {
    this.dispatchContacts.splice(index, 1);
    this.syncDispatchContacts();
    this.onBillToChange();
  }

  syncDispatchContacts() {
    if (this.selectedBillTo) {
      this.selectedBillTo.dispatchContacts = [...this.dispatchContacts];
    }
  }

  // Invoice Emails Methods
  loadInvoiceEmails() {
    if (this.selectedBillTo?.invoiceEmail) {
      // Split semicolon-separated emails into array
      this.invoiceEmails = this.selectedBillTo.invoiceEmail
        .split(";")
        .map((email: string) => email.trim())
        .filter((email: string) => email.length > 0);

      if (this.invoiceEmails.length === 0) {
        this.invoiceEmails = [""];
      }
    } else {
      this.invoiceEmails = [""];
    }
  }

  addInvoiceEmail() {
    this.invoiceEmails.push("");
    this.onBillToChange();
  }

  removeInvoiceEmail(index: number) {
    if (this.invoiceEmails.length > 1) {
      this.invoiceEmails.splice(index, 1);
      this.syncInvoiceEmails();
      this.onBillToChange();
    }
  }

  onInvoiceEmailChange() {
    this.syncInvoiceEmails();
    this.onBillToChange();
  }

  syncInvoiceEmails() {
    if (this.selectedBillTo) {
      // Join emails back with semicolon
      this.selectedBillTo.invoiceEmail = this.invoiceEmails
        .filter((email) => email.trim().length > 0)
        .join(";");
    }
  }

  // Change tracking
  onBillToChange() {
    this.syncDispatchContacts();

    // Track this bill-to as edited if it's an existing bill-to (has an ID and not in newBillTos)
    if (this.selectedBillTo?.id && !this.isNewBillTo(this.selectedBillTo)) {
      this.editedBillToIds.add(this.selectedBillTo.id);
    }

    this.emitAllChanges();
    this.hasChanges.emit(true);
  }

  // Check if a bill-to is newly added (not yet saved to DB)
  private isNewBillTo(billTo: any): boolean {
    return this.newBillTos.some((b) => b === billTo || b.id === billTo.id);
  }

  // Check if the currently selected bill-to is new (public for template)
  isSelectedBillToNew(): boolean {
    if (!this.selectedBillTo) return false;
    return this.selectedBillTo.id?.startsWith("temp-") || this.isNewBillTo(this.selectedBillTo);
  }

  // Add a new bill-to
  addBillTo() {
    const newBillTo = {
      id: `temp-${Date.now()}`, // Temporary ID for tracking
      customerNo: "",
      companyName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      country: "US",
      validFrom: new Date(),
      validTo: null,
      isActive: true,
      invoiceContent: "",
      invoiceEmail: "",
      dispatchContacts: [],
      rates: [],
    };

    if (!this.client.children) {
      this.client.children = [];
    }
    this.client.children.push(newBillTo);
    this.newBillTos.push(newBillTo);

    this.filterBillTos();
    this.selectBillTo(newBillTo);
    this.emitAllChanges();
    this.hasChanges.emit(true);
  }

  // Confirm and remove a bill-to
  confirmRemoveBillTo(billTo: any) {
    if (!billTo) return;

    const name = billTo.companyName || billTo.customerNo || "this Bill To";
    if (confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      this.removeBillTo(billTo);
    }
  }

  // Remove a bill-to
  removeBillTo(billTo: any) {
    if (!billTo) return;

    // Remove from client.children
    const index = this.client.children.findIndex((b: any) => b === billTo || b.id === billTo.id);
    if (index > -1) {
      this.client.children.splice(index, 1);
    }

    // If it's a new bill-to, just remove from newBillTos
    const newIndex = this.newBillTos.findIndex((b) => b === billTo || b.id === billTo.id);
    if (newIndex > -1) {
      this.newBillTos.splice(newIndex, 1);
    } else if (billTo.id && !billTo.id.startsWith("temp-")) {
      // It's an existing bill-to, add to removed list
      this.removedBillTos.push(billTo);
      // Remove from edited if it was there
      this.editedBillToIds.delete(billTo.id);
    }

    this.filterBillTos();
    this.backToList();
    this.emitAllChanges();
    this.hasChanges.emit(true);
  }

  // Emit all change lists to parent
  private emitAllChanges() {
    // Emit new bill-tos (strip temp IDs)
    const newList = this.newBillTos.map((b) => {
      const copy = { ...b };
      if (copy.id?.startsWith("temp-")) {
        delete copy.id;
      }
      return copy;
    });
    this.newBillTosChange.emit(newList);

    // Emit edited bill-tos
    if (this.client?.children) {
      const editedBillTos = this.client.children.filter(
        (billTo: any) =>
          billTo.id && !billTo.id.startsWith("temp-") && this.editedBillToIds.has(billTo.id)
      );
      this.editedBillTosChange.emit(editedBillTos);
    }

    // Emit removed bill-tos
    this.removedBillTosChange.emit(this.removedBillTos);
  }

  // Get lists for parent (called directly if needed)
  getNewBillTos(): any[] {
    return this.newBillTos.map((b) => {
      const copy = { ...b };
      if (copy.id?.startsWith("temp-")) {
        delete copy.id;
      }
      return copy;
    });
  }

  getEditedBillTos(): any[] {
    if (!this.client?.children) return [];
    return this.client.children.filter(
      (billTo: any) =>
        billTo.id && !billTo.id.startsWith("temp-") && this.editedBillToIds.has(billTo.id)
    );
  }

  getRemovedBillTos(): any[] {
    return this.removedBillTos;
  }

  // Clear all tracking (called after successful save)
  clearAllTracking() {
    this.newBillTos = [];
    this.editedBillToIds.clear();
    this.removedBillTos = [];
  }

  trackByIndex(index: number): number {
    return index;
  }
}
